import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppLayout, AuthLayout } from './layouts';
import { useAuth } from './hooks';
import { WebSocketProvider } from './providers/WebSocketProvider';
import {
  Auth,
  AuthCallback,
  LinkAccounts,
  AcceptInvite,
  BusinessCenter,
  Agents,
  AgentDetail,
  AgentBuilderCreate,
  Applications,
  ApplicationDetail,
  DataSources,
  Analytics,
  Profile,
  Payments,
  Settings,
  Mcp,
  Staff,
  SuperUser,
  SessionReplay,
} from './screens';
import { RequireStaff, RequireSuperUser } from './components/RequireOperator';
import { RequireOrgBilling } from './components/RequireOrgBilling';
import OauthConsent from './screens/OauthConsent';

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuth();
  const location = useLocation();

  if (isInitializing) {
    return null;
  }

  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}`;
    return (
      <Navigate to={`/auth?returnTo=${encodeURIComponent(returnTo)}`} replace />
    );
  }

  return <>{children}</>;
};

const RedirectIfAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuth();
  const location = useLocation();

  if (isInitializing) {
    return null;
  }

  if (isAuthenticated) {
    const params = new URLSearchParams(location.search);
    const returnTo = params.get('returnTo');
    return <Navigate to={returnTo || '/agents'} replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => (
  <Routes>
    <Route path="/invite/:inviteCode" element={<AcceptInvite />} />

    <Route element={<AuthLayout />}>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/link" element={<LinkAccounts />} />
    </Route>

    <Route
      element={
        <RedirectIfAuth>
          <AuthLayout />
        </RedirectIfAuth>
      }
    >
      <Route path="/auth" element={<Auth />} />
    </Route>

    <Route
      path="/oauth/consent"
      element={
        <RequireAuth>
          <OauthConsent />
        </RequireAuth>
      }
    />

    <Route
      element={
        <RequireAuth>
          <WebSocketProvider>
            <AppLayout />
          </WebSocketProvider>
        </RequireAuth>
      }
    >
      <Route path="/" element={<Navigate to="/agents" replace />} />
      <Route path="/agents" element={<Agents />} />
      <Route path="/agents/new" element={<AgentBuilderCreate />} />
      <Route path="/agents/:id" element={<AgentDetail />} />
      <Route path="/agents/:id/sessions/:sid" element={<SessionReplay />} />
      <Route path="/applications" element={<Applications />} />
      <Route path="/applications/:id" element={<ApplicationDetail />} />
      <Route path="/partners" element={<Navigate to="/applications" replace />} />
      <Route path="/data-sources" element={<DataSources />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/business" element={<BusinessCenter />} />
      <Route path="/profile" element={<Profile />} />
      <Route
        path="/payments"
        element={
          <RequireOrgBilling>
            <Payments />
          </RequireOrgBilling>
        }
      />
      <Route path="/settings" element={<Settings />} />
      <Route path="/mcp" element={<Mcp />} />
      <Route
        path="/staff"
        element={
          <RequireStaff>
            <Staff />
          </RequireStaff>
        }
      />
      <Route
        path="/super"
        element={
          <RequireSuperUser>
            <SuperUser />
          </RequireSuperUser>
        }
      />
    </Route>

    <Route path="*" element={<Navigate to="/agents" replace />} />
  </Routes>
);

export default App;
